import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { useSelector } from "react-redux";
import {
  previewFinalDeliveryNotice,
  sendFinalDeliveryNotice,
} from "../../../../../api/finalDelivery";
import { formatDate } from "../../../../../utils/getEntitiesUtils";
import { FINAL_DOCUMENTS } from "../../../../../utils/finalDocuments";

const Recipients = ({ label, emails }) => {
  if (!emails || emails.length === 0) return null;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, mb: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: "bold", mr: 0.5, minWidth: 40 }}>
        {label}
      </Typography>
      {emails.map((email) => (
        <Chip key={email} label={email} size="small" variant="outlined" />
      ))}
    </Box>
  );
};

// Fecha local en AAAA-MM-DD, que es lo que usa <input type="date">. No
// toISOString(): pasa a UTC y de noche en Argentina daría el día siguiente.
const toInputDate = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const inDays = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return toInputDate(date);
};

// "2026-12-11" -> "11/12/2026", sin pasar por Date (y por la zona horaria).
const showDate = (isoDate) => (isoDate ? isoDate.split("-").reverse().join("/") : "-");

const History = ({ history }) => {
  if (!history || history.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Todavía no se mandó ningún aviso a este equipo.
      </Typography>
    );
  }
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Fecha</TableCell>
          <TableCell>Envió</TableCell>
          <TableCell>Destinatarios</TableCell>
          <TableCell>Responder a</TableCell>
          <TableCell>Fecha límite</TableCell>
          <TableCell>Estado</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {history.map((record) => (
          <TableRow key={record.id}>
            <TableCell>{formatDate(record.sent_at)}</TableCell>
            <TableCell>{record.sent_by || "-"}</TableCell>
            <TableCell sx={{ wordBreak: "break-word" }}>
              {[...record.to, ...record.cc].join(", ")}
              {record.bcc.length > 0 && ` (+${record.bcc.length} CCO)`}
            </TableCell>
            <TableCell>{record.reply_to || "-"}</TableCell>
            <TableCell>{showDate(record.review_deadline)}</TableCell>
            <TableCell>
              <Chip
                size="small"
                label={record.sent ? "Enviado" : "Falló"}
                color={record.sent ? "success" : "warning"}
                variant="outlined"
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

/**
 * Vista previa, envío e historial del aviso de entrega final de un equipo.
 *
 * Como el aviso de revisor, pide al backend el mail ya rellenado (mismo código
 * que el envío) y lo muestra. Además deja elegir, para este envío, el Reply-To
 * (prellenado con el de la plantilla o, si no tiene, con el mail del admin) y
 * la fecha límite de evaluación que va en ${fecha_limite} (prellenada a dos
 * semanas). Cambiar la fecha vuelve a pedir la vista previa. Cada envío, salga
 * o no, queda en el historial de abajo.
 */
const FinalDeliveryNoticeDialog = ({ open, team, onClose, onSent, onEditTemplate }) => {
  const user = useSelector((state) => state.user);
  const [preview, setPreview] = useState(null);
  const [replyTo, setReplyTo] = useState("");
  const [deadline, setDeadline] = useState("");
  const replyToLoaded = useRef(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // Al abrir para un equipo: estado limpio y fecha por defecto.
  useEffect(() => {
    if (!open || !team) return;
    setPreview(null);
    setError(null);
    setResult(null);
    setReplyTo("");
    replyToLoaded.current = false;
    setDeadline(inDays(14));
  }, [open, team]);

  // Vista previa: al abrir y cada vez que cambia la fecha. El Reply-To se toma
  // solo de la primera, para no pisar lo que el admin ya escribió.
  useEffect(() => {
    if (!open || !team || !deadline) return;
    let cancelled = false;
    setError(null);
    setLoading(true);
    previewFinalDeliveryNotice(user, team.id, deadline)
      .then((data) => {
        if (cancelled) return;
        setPreview(data);
        if (!replyToLoaded.current) {
          setReplyTo(data.reply_to || "");
          replyToLoaded.current = true;
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "No se pudo armar la vista previa del aviso.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, team, user, deadline]);

  const handleSend = async () => {
    setSending(true);
    setError(null);
    try {
      const data = await sendFinalDeliveryNotice(user, team.id, {
        replyTo: replyTo.trim(),
        reviewDeadline: preview?.requires_deadline ? deadline : null,
      });
      setResult(data);
      setPreview((prev) => ({ ...prev, history: data.history }));
      if (data.sent) {
        onSent?.(team.id, data);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "No se pudo enviar el aviso. Inténtalo de nuevo.");
    } finally {
      setSending(false);
    }
  };

  const alreadySent = preview?.history?.some((record) => record.sent);
  const missingDeadline = preview?.requires_deadline && !deadline;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Aviso de entrega final
        {team && (
          <Typography variant="subtitle1" color="text.secondary">
            Equipo {team.group_number}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {loading && !preview && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {result?.sent && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {result.detail} Registrado el {formatDate(result.sent_at)}.
          </Alert>
        )}
        {result && !result.sent && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {result.detail}
          </Alert>
        )}

        {preview && (
          <>
            {alreadySent && !result?.sent && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Este aviso ya se mandó. Podés volver a enviarlo: salen los mismos links y
                queda otra entrada en el historial.
              </Alert>
            )}

            <Recipients label="Para" emails={preview.to} />
            <Recipients label="CC" emails={preview.cc} />
            <Recipients label="CCO" emails={preview.bcc} />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Responder a (Reply-To)"
                value={replyTo}
                onChange={(e) => setReplyTo(e.target.value)}
                size="small"
                fullWidth
                helperText="Las respuestas a este mail llegan acá. Sin esto rebotan: el remitente es no-reply."
              />
              {preview.requires_deadline && (
                <TextField
                  label="Fecha límite de evaluación"
                  type="date"
                  value={deadline}
                  onChange={(e) => setDeadline(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: toInputDate(new Date()) }}
                  sx={{ minWidth: 220 }}
                  error={missingDeadline}
                  helperText={<>Va en el mail donde dice <code>$&#123;fecha_limite&#125;</code>.</>}
                />
              )}
            </Stack>

            <Divider sx={{ my: 1.5 }} />

            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Asunto:</strong> {preview.subject}
            </Typography>

            <Paper variant="outlined" sx={{ p: 2, backgroundColor: "#fafafa" }}>
              <Typography
                variant="body2"
                component="div"
                sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
              >
                {preview.body_text}
              </Typography>
            </Paper>

            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1.5 }} flexWrap="wrap">
              <Typography variant="caption" color="text.secondary">
                Links públicos (cualquiera con el link puede descargar):
              </Typography>
              {FINAL_DOCUMENTS.map((document) => (
                <Link
                  key={document.key}
                  href={preview.links[document.key]}
                  target="_blank"
                  rel="noopener"
                  variant="caption"
                >
                  {document.label}
                </Link>
              ))}
            </Stack>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Historial de envíos
            </Typography>
            <History history={preview.history} />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Button onClick={onEditTemplate} size="small">
          Editar plantilla
        </Button>
        <Box>
          <Button onClick={onClose} color="inherit" sx={{ mr: 1 }}>
            {result?.sent ? "Cerrar" : "Cancelar"}
          </Button>
          <Button
            onClick={handleSend}
            variant="contained"
            startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            disabled={!preview || sending || loading || missingDeadline}
          >
            {alreadySent || result?.sent ? "Reenviar" : "Enviar"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default FinalDeliveryNoticeDialog;
